
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, User, Calendar, IndianRupee, CreditCard, Truck, MapPin, Phone, Mail, Clock, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import CustomDropdown from "@/components/admin/CustomDropdown";
import { motion } from "framer-motion";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  processing: "bg-purple-100 text-purple-700",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-700",
};

export default function OrderDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const orderId = params.id as string;
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token && orderId) {
      fetchOrderDetails();
    }
  }, [token, orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getOrder(parseInt(orderId), token!);
      // Backend wraps response in: { success, message, data: { ... } }
      const wrappedResponse = response;
      const orderData = wrappedResponse.data || wrappedResponse;
      setOrder(orderData);
    } catch (error: any) {
      console.error("Error fetching order details:", error);
      toast.error(error.response?.data?.detail || "Failed to load order details");
      navigate("/admin/orders");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!token || !order) return;
    try {
      await adminApi.updateOrderStatus(order.id, newStatus, token);
      toast.success("Order status updated successfully");
      fetchOrderDetails();
    } catch (error: any) {
      console.error("Error updating order status:", error);
      toast.error(error.response?.data?.detail || "Failed to update order status");
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-2" />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">Order not found</p>
          <Link
            to="/admin/orders"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <Link
          to="/admin/orders"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-xl font-bold font-playfair mb-0.5">Order Details</h1>
          <p className="text-xs text-gray-500">Order #{order.order_number}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Order Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h2 className="text-sm font-bold text-gray-900">Order Items</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {order.items && order.items.length > 0 ? (
                order.items.map((item: any) => (
                  <div key={item.id} className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                        <p className="text-xs text-gray-500">Quantity: {item.quantity} × ₹{item.price.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">₹{item.subtotal.toLocaleString()}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-sm text-gray-500">No items found</div>
              )}
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">Total Amount</span>
              <span className="text-lg font-bold text-gray-900">₹{order.total_amount?.toLocaleString() || 0}</span>
            </div>
          </motion.div>

          {/* Order Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Order Timeline
            </h2>
            <div className="space-y-3 relative">
              {order.timeline && order.timeline.length > 0 ? (
                order.timeline.map((entry: any, index: number) => {
                  const isLast = index === order.timeline.length - 1;
                  const statusColors: Record<string, string> = {
                    pending: "bg-yellow-500",
                    confirmed: "bg-blue-500",
                    processing: "bg-purple-500",
                    shipped: "bg-indigo-500",
                    delivered: "bg-green-500",
                    cancelled: "bg-red-500",
                  };
                  const statusColor = statusColors[entry.status] || "bg-gray-500";
                  
                  return (
                    <div key={entry.id} className="flex items-start gap-3 relative">
                      <div className="relative flex flex-col items-center">
                        <div className={`w-2.5 h-2.5 ${statusColor} rounded-full z-10`} />
                        {!isLast && (
                          <div className="absolute top-2.5 left-1/2 transform -translate-x-1/2 w-0.5 h-8 bg-gray-200" />
                        )}
                      </div>
                      <div className="flex-1 pb-3">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium text-gray-900 capitalize">{entry.status}</p>
                          {isLast && <CheckCircle className="w-3 h-3 text-green-500" />}
                        </div>
                        {entry.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{entry.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(entry.created_at).toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-gray-500">No timeline entries found</div>
              )}
            </div>
          </motion.div>

          {/* Delivery Address */}
          {order.address && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Delivery Address
              </h2>
              <div className="space-y-2 text-xs">
                {order.address.name && (
                  <p className="font-medium text-gray-900">{order.address.name}</p>
                )}
                {order.address.phone && (
                  <p className="text-gray-600 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {order.address.phone}
                  </p>
                )}
                {order.address.address_line1 && (
                  <p className="text-gray-600">{order.address.address_line1}</p>
                )}
                {order.address.address_line2 && (
                  <p className="text-gray-600">{order.address.address_line2}</p>
                )}
                <div className="flex flex-wrap gap-1 text-gray-600">
                  {order.address.city && <span>{order.address.city},</span>}
                  {order.address.state && <span>{order.address.state}</span>}
                  {order.address.pincode && <span>- {order.address.pincode}</span>}
                </div>
                {order.address.country && (
                  <p className="text-gray-600">{order.address.country}</p>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Order Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <h2 className="text-sm font-bold text-gray-900 mb-3">Order Status</h2>
            <CustomDropdown
              value={order.status}
              onChange={handleStatusUpdate}
              options={[
                { value: "pending", label: "Pending", className: "bg-yellow-100 text-yellow-700" },
                { value: "confirmed", label: "Confirmed", className: "bg-blue-100 text-blue-700" },
                { value: "processing", label: "Processing", className: "bg-purple-100 text-purple-700" },
                { value: "shipped", label: "Shipped", className: "bg-indigo-100 text-indigo-700" },
                { value: "delivered", label: "Delivered", className: "bg-green-100 text-green-700" },
                { value: "cancelled", label: "Cancelled", className: "bg-red-100 text-red-700" },
              ]}
              className="w-full"
            />
          </motion.div>

          {/* Customer Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <h2 className="text-sm font-bold text-gray-900 mb-3">Customer Information</h2>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs font-medium text-gray-900">
                    {order.user?.first_name || ""} {order.user?.last_name || ""}
                  </p>
                </div>
              </div>
              {order.user?.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <p className="text-xs text-gray-600">{order.user.email}</p>
                </div>
              )}
              {order.user?.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <p className="text-xs text-gray-600">{order.user.phone}</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <h2 className="text-sm font-bold text-gray-900 mb-3">Order Summary</h2>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Order Number</span>
                <span className="font-medium text-gray-900">{order.order_number}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Date</span>
                <span className="font-medium text-gray-900">
                  {order.created_at
                    ? new Date(order.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Payment Status</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    order.payment_status === "completed"
                      ? "bg-green-100 text-green-700"
                      : order.payment_status === "pending"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {order.payment_status || "—"}
                </span>
              </div>
              <div className="pt-2 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Total</span>
                <span className="text-lg font-bold text-gray-900">₹{order.total_amount?.toLocaleString() || 0}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
